"use client";

import Link from "next/link";
import { Building2, CalendarDays, Euro, Landmark, Languages, MapPin, Users } from "lucide-react";
import DownloadCTA from "@/components/shared/DownloadCTA";
import HoverArrow from "@/components/shared/HoverArrow";
import { semesterLabel, type University } from "@/lib/universities";

function FactRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof MapPin;
  label: string;
  value: string;
}) {
  return (
    <li className="flex items-start justify-between gap-4 py-2.5 border-b border-line last:border-b-0 text-sm">
      <span className="inline-flex items-center gap-2 text-ink-faint shrink-0">
        <Icon size={14} className="text-accent" aria-hidden="true" />
        {label}
      </span>
      <span className="font-semibold text-ink text-right leading-snug">{value}</span>
    </li>
  );
}

/** Sticky Quick-Facts + CTA (Desktop-Sidebar der Detailseite). */
export default function UniDetailSidebar({ university }: { university: University }) {
  return (
    <div className="sticky top-24 flex flex-col gap-4">
      <div className="card-flat bg-white/60 p-6">
        <p className="text-xs font-bold uppercase tracking-widest text-ink-faint mb-3">
          Quick Facts
        </p>
        <ul>
          <FactRow icon={MapPin} label="Stadt" value={university.city} />
          <FactRow icon={Building2} label="Bundesland" value={university.state} />
          <FactRow icon={Landmark} label="Einrichtung" value={university.type} />
          <FactRow
            icon={CalendarDays}
            label="Studienbeginn"
            value={semesterLabel(university.semesters)}
          />
          <FactRow icon={Languages} label="Sprache" value={university.language} />
          <FactRow
            icon={Euro}
            label="Gebühren"
            value={university.tuition ?? "Keine"}
          />
          {university.seats && (
            <FactRow icon={Users} label="Studienplätze" value={`${university.seats} / Jahr`} />
          )}
        </ul>
      </div>

      <div className="card-flat bg-accent text-paper p-6 relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 80% 90% at 50% 0%, rgba(124,58,237,0.35) 0%, transparent 70%)",
          }}
        />
        <div className="relative flex flex-col gap-3">
          <h3 className="font-display font-bold text-xl leading-tight">
            Reichen deine Werte für {university.city}?
          </h3>
          <p className="text-sm text-paper/80 leading-relaxed">
            Gib deine Noten in die ncrechner App ein und sieh sofort, wie deine
            Chancen an der {university.name} stehen.
          </p>
          <DownloadCTA
            variant="paper"
            label="Chancen berechnen"
            className="w-full text-sm py-3 min-h-[46px]"
          />
        </div>
      </div>

      <Link
        href="/unis-in-deutschland/"
        className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-accent hover:text-accent-deep transition-colors group"
      >
        Zur Uni-Übersicht
        <HoverArrow size={14} />
      </Link>
    </div>
  );
}
